import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, AlertTriangle } from "lucide-react";
import { LOGO_CRESCENT } from "@/components/landing/data";
import { useCart } from "@/components/landing/cart/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", order: null });
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error" });
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          clear();
          setState({ phase: "paid", order: r.data });
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) {
          setState({ phase: "error" });
          return;
        }
      } catch {
        setState({ phase: "error" });
        return;
      }
      if (attempts >= MAX_POLLS) {
        setState({ phase: "timeout" });
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="bg-[#ffffff] text-[#0a0a0b] min-h-screen flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 hero-radial" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md bg-[#faf9f6] border border-[#e3e2dc] rounded-3xl p-10 text-center"
      >
        <img src={LOGO_CRESCENT} alt="Deen District" className="h-12 w-12 mx-auto mb-6" />
        {state.phase === "checking" && <Status icon={<Loader2 className="animate-spin" />} title="Confirming your pre-order" text="One moment while we confirm your payment." testId="payment-checking" />}
        {state.phase === "paid" && (
          <>
            <Status icon={<Check strokeWidth={3} />} pink title="Pre-order confirmed" text={`Your piece from The Origin Collection is reserved. A receipt from ${state.order.provider === "paypal" ? "PayPal" : "Stripe"} is on its way to your inbox.`} testId="payment-confirmed" />
            <div data-testid="order-summary" className="mt-6 text-left bg-[#ffffff] border border-[#e3e2dc] rounded-2xl p-5 space-y-2 text-sm">
              <Row label="Order no." value={<span data-testid="order-number" className="font-mono-d tracking-wider">#{sessionId.slice(-8).toUpperCase()}</span>} />
              {state.order.items?.length > 1 ? state.order.items.map((i, idx) => (
                <Row key={idx} label={i.product_name} value={[i.size && i.size !== "ONE SIZE" ? `Size ${i.size}` : null, i.color, i.quantity > 1 ? `×${i.quantity}` : null].filter(Boolean).join(" · ") || "—"} />
              )) : (
                <>
                  <Row label="Piece" value={state.order.product_name} />
                  <Row label="Size" value={state.order.size} />
                  {state.order.color && <Row label="Colour" value={state.order.color} />}
                </>
              )}
              {state.order.amount_discount > 0 && <Row label="Discount" value={`-$${(state.order.amount_discount / 100).toFixed(2)}`} />}
              {state.order.donation_cents > 0 && <Row label="Donation · Palestine" value={<span data-testid="order-donation">${(state.order.donation_cents / 100).toFixed(2)}</span>} />}
              {state.order.shipping_rate && <Row label={`Delivery · ${state.order.shipping_rate}`} value={state.order.amount_shipping ? `$${(state.order.amount_shipping / 100).toFixed(2)}` : "Free"} />}
              <Row label="Paid" value={`$${((state.order.amount_total || 0) / 100).toFixed(2)} ${(state.order.currency || "usd").toUpperCase()} · ${state.order.provider === "paypal" ? "PayPal" : "Card"}`} />
            </div>
            <p className="mt-4 text-xs text-[#5f5e5a] leading-relaxed">Save your order number — with your email it lets you <Link data-testid="success-track-link" to={`/track-order?ref=${sessionId.slice(-8).toUpperCase()}`} className="text-[#ff2a85] underline underline-offset-2">track your order</Link> any time.</p>
          </>
        )}
        {state.phase === "timeout" && <Status icon={<Loader2 />} title="Still processing" text="Your payment is being processed. You'll receive a receipt by email once it clears." testId="payment-timeout" />}
        {state.phase === "error" && <Status icon={<AlertTriangle />} title="We couldn't confirm this payment" text="If you were charged, your Stripe or PayPal receipt is your proof of pre-order. Otherwise, try again from the lookbook." testId="payment-error" />}
        <Link data-testid="back-home-link" to="/" className="mt-8 inline-block px-8 py-4 rounded-full bg-[#0a0a0b] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] hover:bg-[#ff2a85] transition-colors">
          Back to Drop 001
        </Link>
      </motion.div>
    </main>
  );
}

const Status = ({ icon, title, text, pink, testId }) => (
  <div data-testid={testId}>
    <div className={`h-14 w-14 mx-auto rounded-full flex items-center justify-center ${pink ? "bg-[#ff2a85] text-white" : "bg-[#f3f2ec] text-[#0a0a0b]"}`}>{icon}</div>
    <h1 className="font-display font-extrabold uppercase text-3xl mt-6 leading-none">{title}</h1>
    <p className="text-[#5f5e5a] text-sm mt-3 leading-relaxed">{text}</p>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a]">{label}</span>
    <span className="font-medium text-right">{value || "—"}</span>
  </div>
);
